// @ts-strict-ignore
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Dialog, toast } from '@node-space/ui-components'
import useCSATSurvey from 'pages/PaymentSupport/hooks/useCSATSurvey'
import { CSATOption } from './CSATOption'
import {
  CSATCustomerType,
  CSATFeedbackRating,
  CSATFeedbackRatingType,
  CSATFeedbackValue,
  CSATOptionStatus,
  CSATOptionType,
} from './types'

const CSAT_API_URL = process.env.CSAT_API_URL
const CSAT_CP_API_KEY = process.env.CSAT_CP_API_KEY

export type CSATProps = {
  customer: CSATCustomerType
  onClose: () => void
}

export const CSATModal = ({ customer, onClose }: CSATProps) => {
  const { t } = useTranslation()
  const { CSATPreferenceData, CSATPreferenceUpdate, hasNoCSATPreferenceData } = useCSATSurvey()

  const [rating, setRating] = useState<CSATFeedbackRatingType>()
  const [isLoading, setIsLoading] = useState(false)

  const feedbackOptions: CSATOptionType[] = [
    {
      label: t('csat.options.bad'),
      value: CSATFeedbackValue.BAD,
      rating: CSATFeedbackRating.BAD,
    },
    {
      label: t('csat.options.average'),
      value: CSATFeedbackValue.OKAY,
      rating: CSATFeedbackRating.OKAY,
    },
    {
      label: t('csat.options.good'),
      value: CSATFeedbackValue.GOOD,
      rating: CSATFeedbackRating.GOOD,
    },
  ]

  const params = {
    source: `generic_embed`,
    customer_email: `${customer?.emailAddress}`,
    t_firstname: `${customer?.firstname}`,
    t_lastname: `${customer?.lastname}`,
    t_customer_id: `${customer?.customerID}`,
    t_companyName: `${customer?.companyName}`,
  }
  const paramList = new URLSearchParams(params).toString()

  const CSATsurveyUrl = `${CSAT_API_URL}/api/embed/rating/${rating}/${CSAT_CP_API_KEY}/?${paramList}`

  const onSubmitFeedback = () => {
    handleCSATSurveyUpdate()
  }

  const handleCSATSurveyUpdate = () => {
    if (!hasNoCSATPreferenceData) {
      setIsLoading(true)
      const updatedPreferences = { ...CSATPreferenceData }
      updatedPreferences.isSurveyCompleted = true
      CSATPreferenceUpdate(updatedPreferences, onSuccess, onFailure)
    }
  }

  const onSuccess = () => {
    window.open(CSATsurveyUrl)
    handleCloseModal()
  }

  const onFailure = () => {
    handleCloseModal()
    toast({ header: t('csat.errorFeedback'), variant: 'error' })
  }

  const handleCloseModal = () => {
    setIsLoading(false)
    onClose()
  }

  const getStatus = option => {
    return !rating
      ? CSATOptionStatus.DEFAULT
      : rating === option?.rating
        ? CSATOptionStatus.SELECTED
        : CSATOptionStatus.UNSELECTED
  }

  return (
    <Dialog
      title={t('csat.title')}
      open
      onClose={onClose}
      ActionButtons={[
        <Button
          key="send-feedback-button"
          onClick={onSubmitFeedback}
          disabled={!rating || isLoading}
        >
          {t('csat.sendFeedback')}
        </Button>,
      ]}
    >
      <Box flexCol gap={32}>
        <p className="text-foreground-prominent text-body-regular text-center">{t('csat.intro')}</p>

        <Box flex justify="between" paddingX={20}>
          {feedbackOptions?.map(option => (
            <CSATOption
              key={option?.value}
              label={option?.label}
              value={option?.value}
              status={getStatus(option)}
              onClick={() => !isLoading && setRating(option?.rating)}
            />
          ))}
        </Box>

        <p className="text-foreground-prominent text-body-regular text-center">
          {t('csat.additionalText')}
        </p>
      </Box>
    </Dialog>
  )
}
